// Archivo: inventory/IngresosInventario.js

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- CAMBIO: Importamos el Modal y el nuevo componente de detalle ---
import Modal from '../../ui/Modal';
import BatchDetailView from '../../ui/BatchDetailView';

const PrintIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2-4h6m-6-4v2m6-4v2" /></svg>;
// --- CAMBIO: Añadimos un icono de Ojo ---
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;


export default function IngresosInventario({ db, showModal, currentUser, catalogs }) {
    const [entries, setEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // --- CAMBIO: Estados para manejar el modal de detalle ---
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedBatchForDetail, setSelectedBatchForDetail] = useState(null);

    useEffect(() => {
        const q = query(collection(db, 'inventory_entries'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const entriesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEntries(entriesList);
            setIsLoading(false);
        }, (error) => {
            showModal("Error", "No se pudo cargar el historial de ingresos.");
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, showModal]);

    const groupedEntries = useMemo(() => {
        if (!catalogs || entries.length === 0) return [];
        const groups = entries.reduce((acc, entry) => {
            const batchId = entry.batchId || entry.id;
            if (!acc[batchId]) {
                acc[batchId] = {
                    batchId: batchId, createdBy: entry.createdBy,
                    createdAt: entry.createdAt,
                    createdAtFormatted: entry.createdAt?.toDate().toLocaleString('es-GT', { timeZone: 'America/Guatemala' }),
                    items: [], totalQuantity: 0,
                };
            }
            const product = catalogs.products.find(p => p.id === entry.productId);
            const design = catalogs.designs.find(d => d.id === entry.designId);
            acc[batchId].items.push({
                ...entry,
                productName: product?.name || 'N/A',
                designName: design ? `${design.name} (${entry.designSize || ''})` : 'Sin Diseño',
            });
            acc[batchId].totalQuantity += entry.quantity;
            return acc;
        }, {});
        return Object.values(groups);
    }, [entries, catalogs]);
    
    // --- CAMBIO: Nueva función para abrir el modal con los detalles del lote ---
    const handleViewDetails = (batch) => {
        setSelectedBatchForDetail(batch);
        setIsDetailModalOpen(true);
    };

    const handlePrintBatch = (batch) => {
        const doc = new jsPDF();
        const logoImg = document.getElementById('company-logo-for-pdf');
        if (logoImg) { doc.addImage(logoImg, 'PNG', 15, 12, 50, 15); }
        doc.setFontSize(18);
        doc.text(`Comprobante de Ingreso de Lote`, 15, 40);
        doc.setFontSize(12);
        doc.text(`ID del Lote: ${batch.batchId}`, 15, 47);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Registrado por: ${batch.createdBy}`, 15, 54);
        doc.text(`Fecha de Registro: ${batch.createdAtFormatted}`, 15, 59);
        doc.text(`Generado por: ${currentUser.name} el ${new Date().toLocaleString('es-GT', { timeZone: 'America/Guatemala' })}`, 15, 64);
        const tableColumns = ["Producto", "Diseño", "Talla", "Color", "Cantidad"];
        const tableRows = batch.items.map(item => [
            item.productName, item.designName, item.size, item.color, item.quantity
        ]);
        autoTable(doc, {
            head: [tableColumns], body: tableRows, startY: 75, headStyles: { fillColor: [22, 160, 133] },
        });
        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Cantidad Total de Productos en el Lote: ${batch.totalQuantity}`, 15, finalY + 10);
        doc.save(`ingreso-lote-${batch.batchId}.pdf`);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <img id="company-logo-for-pdf" src="/aralellum.png" style={{ display: 'none' }} alt="Logo de la empresa"/>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Historial de Ingresos a Stock</h3>
            <p className="text-gray-600 mb-6">Aquí puedes ver todos los lotes de ingreso que se han registrado en el sistema.</p>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">Fecha</th>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">ID del Lote</th>
                            <th className="p-3 text-left text-sm font-semibold text-gray-600">Registrado Por</th>
                            <th className="p-3 text-right text-sm font-semibold text-gray-600">Nº de Ítems</th>
                            <th className="p-3 text-right text-sm font-semibold text-gray-600">Cantidad Total</th>
                            <th className="p-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (<tr><td colSpan="6" className="text-center p-4">Cargando historial...</td></tr>)
                            : groupedEntries.map(batch => (
                                <tr key={batch.batchId} className="border-b hover:bg-sky-50">
                                    <td className="p-3 text-sm">{batch.createdAtFormatted}</td>
                                    <td className="p-3 font-mono text-xs">{batch.batchId}</td>
                                    <td className="p-3">{batch.createdBy}</td>
                                    <td className="p-3 text-right">{batch.items.length}</td>
                                    <td className="p-3 text-right font-bold">{batch.totalQuantity}</td>
                                    <td className="p-3 text-center">
                                        {/* --- CAMBIO: Añadimos el botón de Ver Detalles --- */}
                                        <div className="flex justify-center items-center gap-4">
                                            <button onClick={() => handleViewDetails(batch)} title="Ver Detalles del Lote">
                                                <EyeIcon />
                                            </button>
                                            <button onClick={() => handlePrintBatch(batch)} title="Imprimir Comprobante del Lote">
                                                <PrintIcon />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                {groupedEntries.length === 0 && !isLoading && <p className="text-center text-gray-500 py-8">No hay ingresos registrados.</p>}
            </div>

            {/* --- CAMBIO: Añadimos el Modal que mostrará los detalles --- */}
            <Modal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} title="Detalle de Lote de Ingreso">
                <BatchDetailView batch={selectedBatchForDetail} currentUser={currentUser} />
            </Modal>
        </div>
    );
}